import { LitElement, css, html } from 'lit';
import { connectEventsSocket, fetchChannelConfig, sendMessage, sendTyping } from './client';
import type { ChatMessage, WebchatConfig, WebchatPosition } from './types';

const DEFAULTS = {
  mode: 'widget',
  position: 'bottom-right',
  title: 'Keebai Assistant',
  placeholder: 'Escribe tu mensaje...',
  sendButtonText: 'Enviar',
  launcherSymbol: '◉',
  zIndex: 2147483000,
  gatewayUrl: 'https://webchat.keebai.com',
} as const;

type ResolvedConfig = Required<Omit<WebchatConfig, 'theme' | 'ui'>> & Pick<WebchatConfig, 'theme' | 'ui'>;

const positionMap: Record<WebchatPosition, Record<string, string>> = {
  'top-left': { top: '18px', left: '18px' },
  'top-center': { top: '18px', left: '50%', transform: 'translateX(-50%)' },
  'top-right': { top: '18px', right: '18px' },
  'center-left': { top: '50%', left: '18px', transform: 'translateY(-50%)' },
  center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
  'center-right': { top: '50%', right: '18px', transform: 'translateY(-50%)' },
  'bottom-left': { bottom: '18px', left: '18px' },
  'bottom-center': { bottom: '18px', left: '50%', transform: 'translateX(-50%)' },
  'bottom-right': { bottom: '18px', right: '18px' },
};

function randomId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

class KeebaiWebchatElement extends LitElement {
  static properties = {
    config: { attribute: false },
  };

  static styles = css`
    :host {
      all: initial;
      position: fixed;
      display: block;
      color: #e7edf8;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      z-index: var(--kb-z, 2147483000);
    }

    .shell {
      position: relative;
      display: block;
      min-width: 64px;
      min-height: 64px;
      animation: fade-in 180ms ease-out;
    }

    .launcher {
      border: 0;
      width: var(--kb-launcher-size, 62px);
      height: var(--kb-launcher-size, 62px);
      display: grid;
      place-items: center;
      border-radius: var(--kb-launcher-radius, 999px);
      clip-path: var(--kb-launcher-clip, none);
      cursor: pointer;
      color: #ecf5ff;
      font-size: calc(var(--kb-launcher-size, 62px) * 0.45);
      background: var(--kb-launcher-bg, var(--kb-accent, #17b2cc));
      box-shadow: 0 20px 36px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.24);
      transition: transform 140ms ease, box-shadow 140ms ease;
      overflow: hidden;
    }

    .launcher:hover {
      transform: translateY(-2px);
      box-shadow: 0 24px 44px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.24);
    }

    .panel {
      width: min(var(--kb-panel-w, 390px), calc(100vw - 24px));
      height: min(var(--kb-panel-h, 650px), calc(100vh - 24px));
      display: flex;
      flex-direction: column;
      border-radius: var(--kb-panel-radius, 24px);
      border: 1px solid rgba(147, 177, 213, 0.22);
      background: var(--kb-surface, #0f1b2e);
      box-shadow: 0 24px 68px rgba(2, 8, 20, 0.58);
      overflow: hidden;
      backdrop-filter: var(--kb-panel-backdrop, blur(14px));
      font-size: var(--kb-font-size, 14px);
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 16px;
      font-weight: 700;
      color: var(--kb-header-text, var(--kb-text, #e7edf8));
      background: var(--kb-accent, #17b2cc);
      border-bottom: 1px solid rgba(140, 165, 200, 0.24);
    }

    .close {
      border: 1px solid rgba(255, 255, 255, 0.35);
      width: 30px;
      height: 30px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.2);
      color: var(--kb-header-text, #ffffff);
      cursor: pointer;
      font-size: 17px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .launcher-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      display: block;
      border-radius: inherit;
      clip-path: inherit;
    }

    .list {
      flex: 1;
      overflow: auto;
      padding: 14px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      scrollbar-width: thin;
      scrollbar-color: rgba(122, 149, 184, 0.6) transparent;
    }

    .bubble {
      max-width: 84%;
      line-height: 1.35;
      font-size: 1em;
      border-radius: var(--kb-bubble-radius, 16px);
      padding: 10px 12px;
      white-space: pre-wrap;
      word-break: break-word;
      animation: bubble-in 170ms ease-out;
    }

    .bubble.user {
      align-self: flex-end;
      color: var(--kb-user-text, #f4fbff);
      background: var(--kb-user-bubble, var(--kb-accent, #17b2cc));
    }

    .bubble.assistant {
      align-self: flex-start;
      color: var(--kb-assistant-text, #d7e7f5);
      border: 1px solid rgba(133, 165, 196, 0.28);
      background: var(--kb-assistant-bubble, rgba(13, 26, 47, 0.8));
    }

    .typing {
      min-height: 20px;
      padding: 0 14px;
      color: #9dc0dd;
      font-size: 12px;
    }

    .composer {
      display: flex;
      gap: 8px;
      padding: 12px;
      border-top: 1px solid rgba(128, 157, 192, 0.22);
      background: var(--kb-composer-bg, color-mix(in srgb, var(--kb-surface, #ffffff) 96%, #d1d5db));
    }

    .input {
      flex: 1;
      border: 1px solid rgba(126, 156, 191, 0.35);
      border-radius: var(--kb-input-radius, 12px);
      outline: none;
      color: var(--kb-input-text, #e5eef9);
      background: var(--kb-input-bg, rgba(6, 12, 22, 0.88));
      font-size: 1em;
      padding: 10px 12px;
    }

    .input:focus {
      border-color: color-mix(in srgb, var(--kb-accent, #17b2cc) 70%, #c8f6ff);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--kb-accent, #17b2cc) 26%, transparent);
    }

    .send {
      border: 0;
      border-radius: var(--kb-input-radius, 12px);
      padding: 0 13px;
      cursor: pointer;
      color: var(--kb-send-text, #ecf8ff);
      background: var(--kb-accent, #17b2cc);
      font-weight: 700;
    }

    .spot {
      width: min(860px, calc(100vw - 28px));
      max-height: min(76vh, 620px);
      display: flex;
      flex-direction: column;
      gap: 10px;
      background: transparent;
    }

    .spot .list {
      padding: 0;
      max-height: min(56vh, 470px);
      overflow: auto;
      background: transparent;
      border: 0;
    }

    .spot .bubble {
      max-width: min(780px, 92vw);
      border-radius: var(--kb-bubble-radius, 14px);
      backdrop-filter: blur(6px);
    }

    .spot .bubble.user {
      align-self: flex-end;
    }

    .spot .bubble.assistant {
      align-self: flex-start;
      border-color: rgba(168, 195, 228, 0.22);
      background: rgba(10, 23, 41, 0.52);
    }

    .spot-input-wrap {
      border: 1px solid rgba(150, 180, 214, 0.35);
      border-radius: 18px;
      padding: 8px;
      backdrop-filter: var(--kb-spot-backdrop, blur(14px));
      background: var(--kb-surface, rgba(8, 14, 29, 0.92));
      box-shadow: 0 18px 48px rgba(1, 6, 16, 0.5);
    }

    .spot .composer {
      padding: 0;
      background: transparent;
      border: 0;
    }

    .spot .typing {
      padding: 0 4px;
      min-height: 16px;
    }

    .powered {
      text-align: center;
      font-size: 11px;
      padding: 4px 8px 8px;
      color: color-mix(in srgb, var(--kb-text, #e7edf8) 62%, #9ca3af);
      letter-spacing: 0.02em;
    }

    .powered a {
      color: inherit;
      text-decoration: none;
      font-weight: 600;
    }

    @keyframes bubble-in {
      from { opacity: 0; transform: translateY(5px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes fade-in {
      from { opacity: 0; transform: translateY(4px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;

  config!: ResolvedConfig;
  private open = false;
  private destroyed = false;
  private bootstrapped = false;
  private stopSocket: (() => void) | null = null;
  private reconnectTimer: number | null = null;
  private typingText = '';
  private messages: ChatMessage[] = [];
  private draft = '';
  private sending = false;
  private ignoreInputUntil = 0;
  private initialized = false;
  private listEl: HTMLDivElement | null = null;
  private inputEl: HTMLInputElement | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.applyPosition();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.destroyed = true;
    if (this.stopSocket) {
      this.stopSocket();
    }
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
    }
  }

  updated(): void {
    this.applyPosition();
    this.tryInitialize();
    this.syncInputValue();
    this.scrollToBottom();
  }

  async bootstrap(inputConfig: WebchatConfig): Promise<void> {
    try {
      const remote = await fetchChannelConfig({
        gatewayUrl: this.config.gatewayUrl,
        company: this.config.company,
        channelId: this.config.channelId,
      });
      this.config = mergeWithRemoteConfig(this.config, inputConfig, remote);
      this.requestUpdate();
    } catch {
      // If remote config is not reachable, keep local/default configuration.
    } finally {
      this.bootstrapped = true;
      this.tryInitialize();
    }
  }

  private tryInitialize(): void {
    if (this.initialized || !this.config || !this.bootstrapped) return;
    this.initialized = true;
    this.open = this.config.autoOpen || this.config.mode === 'spotlight';
    this.startRealtime();
  }

  private applyPosition(): void {
    if (!this.config) return;
    const pos = positionMap[this.config.position];
    Object.entries(pos).forEach(([key, value]) => {
      this.style.setProperty(key, value);
    });
    this.style.setProperty('--kb-z', String(this.config.zIndex));
    if (this.config.theme?.accentColor) this.style.setProperty('--kb-accent', this.config.theme.accentColor);
    const surfaceColor = this.config.theme?.surfaceColor;
    if (surfaceColor) this.style.setProperty('--kb-surface', surfaceColor);
    if (this.config.theme?.textColor) this.style.setProperty('--kb-text', this.config.theme.textColor);
    if (this.config.theme?.headerTextColor) this.style.setProperty('--kb-header-text', this.config.theme.headerTextColor);
    if (this.config.theme?.userBubbleColor) this.style.setProperty('--kb-user-bubble', this.config.theme.userBubbleColor);
    if (this.config.theme?.userTextColor) this.style.setProperty('--kb-user-text', this.config.theme.userTextColor);
    if (this.config.theme?.assistantBubbleColor) this.style.setProperty('--kb-assistant-bubble', this.config.theme.assistantBubbleColor);
    if (this.config.theme?.assistantTextColor) this.style.setProperty('--kb-assistant-text', this.config.theme.assistantTextColor);
    if (this.config.theme?.composerBackgroundColor) this.style.setProperty('--kb-composer-bg', this.config.theme.composerBackgroundColor);
    if (this.config.theme?.inputBackgroundColor) this.style.setProperty('--kb-input-bg', this.config.theme.inputBackgroundColor);
    if (this.config.theme?.inputTextColor) this.style.setProperty('--kb-input-text', this.config.theme.inputTextColor);
    if (this.config.theme?.sendButtonTextColor) this.style.setProperty('--kb-send-text', this.config.theme.sendButtonTextColor);
    if (this.config.ui?.panelWidth) this.style.setProperty('--kb-panel-w', `${this.config.ui.panelWidth}px`);
    if (this.config.ui?.panelHeight) this.style.setProperty('--kb-panel-h', `${this.config.ui.panelHeight}px`);
    if (this.config.ui?.panelRadius) this.style.setProperty('--kb-panel-radius', `${this.config.ui.panelRadius}px`);
    if (this.config.ui?.bubbleRadius) this.style.setProperty('--kb-bubble-radius', `${this.config.ui.bubbleRadius}px`);
    if (this.config.ui?.inputRadius) this.style.setProperty('--kb-input-radius', `${this.config.ui.inputRadius}px`);
    if (this.config.ui?.fontSize) this.style.setProperty('--kb-font-size', `${this.config.ui.fontSize}px`);
    if (this.config.ui?.launcherSize) this.style.setProperty('--kb-launcher-size', `${this.config.ui.launcherSize}px`);
    if (this.config.ui?.launcherBackgroundColor) this.style.setProperty('--kb-launcher-bg', this.config.ui.launcherBackgroundColor);
    const launcherShape = this.config.ui?.launcherShape || 'circle';
    if (launcherShape === 'rounded-square') {
      this.style.setProperty('--kb-launcher-radius', '16px');
      this.style.setProperty('--kb-launcher-clip', 'none');
    } else if (launcherShape === 'squircle') {
      this.style.setProperty('--kb-launcher-radius', '30%');
      this.style.setProperty('--kb-launcher-clip', 'none');
    } else if (launcherShape === 'hexagon') {
      this.style.setProperty('--kb-launcher-radius', '0');
      this.style.setProperty('--kb-launcher-clip', 'polygon(25% 6.7%, 75% 6.7%, 100% 50%, 75% 93.3%, 25% 93.3%, 0% 50%)');
    } else {
      this.style.setProperty('--kb-launcher-radius', '999px');
      this.style.setProperty('--kb-launcher-clip', 'none');
    }

    const isTransparentSurface = typeof surfaceColor === 'string' && surfaceColor.trim().toLowerCase() === 'transparent';
    if (isTransparentSurface) {
      this.style.setProperty('--kb-panel-backdrop', 'none');
      this.style.setProperty('--kb-spot-backdrop', 'none');
    } else {
      this.style.setProperty('--kb-panel-backdrop', 'blur(14px)');
      this.style.setProperty('--kb-spot-backdrop', 'blur(14px)');
    }
  }

  private setOpen(value: boolean): void {
    this.open = value;
    this.requestUpdate();
    if (value) {
      window.setTimeout(() => this.inputEl?.focus(), 30);
    }
  }

  async sendCurrentMessage(forcedContent?: string): Promise<void> {
    const content = String(forcedContent ?? this.draft).trim();
    if (!content || this.sending) return;
    this.sending = true;
    this.clearDraft(false);
    this.messages = [...this.messages, { id: randomId('local'), role: 'user', content, createdAt: new Date().toISOString() }];
    this.requestUpdate();

    try {
      await sendMessage({
        gatewayUrl: this.config.gatewayUrl,
        company: this.config.company,
        channelId: this.config.channelId,
        userId: this.config.userId,
        sessionId: this.config.sessionId,
        content,
      });
    } catch (error) {
      // Keep the sent bubble visible but avoid leaving UI in inconsistent state.
      console.error('Failed to send webchat message', error);
    } finally {
      this.sending = false;
      this.clearDraft();
    }
  }

  private async emitTyping(): Promise<void> {
    try {
      await sendTyping(this.config.gatewayUrl, this.config.company, this.config.channelId, this.config.userId);
    } catch {
      // no-op
    }
  }

  private consumeEvent(event: { event: string; payload: Record<string, any>; at: string; id?: number }): void {
    if (event.event === 'typing') {
      this.typingText = 'Keebai está escribiendo...';
      this.requestUpdate();
      return;
    }
    if (event.event === 'message') {
      const text = String(event.payload.content || event.payload.message?.content || event.payload.message || '').trim();
      if (!text) return;
      this.typingText = '';
      this.messages = [
        ...this.messages,
        {
          id: String(event.payload.external_message_id || randomId('msg')),
          role: 'assistant',
          content: text,
          createdAt: event.at,
        },
      ];
      this.requestUpdate();
    }
  }

  private startRealtime(): void {
    if (this.stopSocket) {
      this.stopSocket();
      this.stopSocket = null;
    }

    this.stopSocket = connectEventsSocket({
      gatewayUrl: this.config.gatewayUrl,
      channelId: this.config.channelId,
      userId: this.config.userId,
      onOpen: () => {
        if (this.reconnectTimer) {
          window.clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      },
      onClose: () => {
        if (this.destroyed) return;
        this.scheduleReconnect();
      },
      onError: () => {
        if (this.destroyed) return;
        this.scheduleReconnect();
      },
      onEvent: (event) => this.consumeEvent(event),
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.destroyed) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.destroyed) {
        this.startRealtime();
      }
    }, 1500);
  }

  private onInput(ev: Event): void {
    const target = ev.target as HTMLInputElement;
    if (this.sending || Date.now() < this.ignoreInputUntil) {
      target.value = '';
      this.draft = '';
      return;
    }
    this.draft = target.value;
    void this.emitTyping();
  }

  private clearDraft(shouldUpdate = true): void {
    this.ignoreInputUntil = Date.now() + 1200;
    this.draft = '';
    if (this.inputEl) {
      this.inputEl.value = '';
    }
    const input = this.renderRoot?.querySelector('input.input') as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
    if (shouldUpdate) {
      this.requestUpdate();
      queueMicrotask(() => {
        if (this.inputEl) this.inputEl.value = '';
        const microtaskInput = this.renderRoot?.querySelector('input.input') as HTMLInputElement | null;
        if (microtaskInput) microtaskInput.value = '';
      });
    }
  }

  private submitComposer(forcedContent?: string): void {
    const rawContent = String(forcedContent ?? this.inputEl?.value ?? this.draft);
    this.clearDraft();
    void this.sendCurrentMessage(rawContent);
  }

  private onKeyDown(ev: KeyboardEvent): void {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      this.submitComposer();
    }
  }

  private syncInputValue(): void {
    if (!this.inputEl) return;
    const nextValue = this.draft || '';
    if (this.inputEl.value !== nextValue) {
      this.inputEl.value = nextValue;
    }
  }

  private scrollToBottom(): void {
    if (!this.listEl) return;
    this.listEl.scrollTop = this.listEl.scrollHeight;
  }

  render() {
    if (!this.config) {
      return html``;
    }

    const messages = this.messages.map(
      (msg) => html`<div class="bubble ${msg.role === 'user' ? 'user' : 'assistant'}">${msg.content}</div>`,
    );

    if (this.config.mode === 'spotlight') {
      return html`
        <div class="shell spot">
          <div class="list" ${((el: Element | undefined) => { this.listEl = (el as HTMLDivElement) || null; }) as any}>${messages}</div>
          <div class="typing">${this.typingText}</div>
          <div class="spot-input-wrap">
            <form class="composer" @submit=${(ev: SubmitEvent) => {
              ev.preventDefault();
              this.submitComposer();
            }}>
              <input
                class="input"
                ${((el: Element | undefined) => { this.inputEl = (el as HTMLInputElement) || null; }) as any}
                .value=${this.draft}
                @input=${this.onInput}
                @keydown=${this.onKeyDown}
                placeholder=${this.config.placeholder}
              />
              <button class="send" type="submit">${this.config.sendButtonText}</button>
            </form>
          </div>
          <div class="powered"><a href="https://keebai.com" target="_blank" rel="noopener noreferrer">Powered by Keebai</a></div>
        </div>
      `;
    }

    return html`
      <div class="shell">
        ${this.open
          ? html`
              <div class="panel">
                <div class="header">
                  <span>${this.config.title}</span>
                  <button class="close" type="button" @click=${() => this.setOpen(false)}>×</button>
                </div>
                <div class="list" ${((el: Element | undefined) => { this.listEl = (el as HTMLDivElement) || null; }) as any}>${messages}</div>
                <div class="typing">${this.typingText}</div>
                <form class="composer" @submit=${(ev: SubmitEvent) => {
                  ev.preventDefault();
                  this.submitComposer();
                }}>
                  <input
                    class="input"
                    ${((el: Element | undefined) => { this.inputEl = (el as HTMLInputElement) || null; }) as any}
                    .value=${this.draft}
                    @input=${this.onInput}
                    @keydown=${this.onKeyDown}
                    placeholder=${this.config.placeholder}
                  />
                  <button class="send" type="submit">${this.config.sendButtonText}</button>
                </form>
                <div class="powered"><a href="https://keebai.com" target="_blank" rel="noopener noreferrer">Powered by Keebai</a></div>
              </div>
            `
          : html`<button class="launcher" type="button" @click=${() => this.setOpen(true)}>
              ${this.config.launcherIconUrl
                ? html`<img class="launcher-img" src=${this.config.launcherIconUrl} alt="Launcher icon" />`
                : this.config.launcherSymbol}
            </button>`}
      </div>
    `;
  }
}

if (!customElements.get('keebai-webchat')) {
  customElements.define('keebai-webchat', KeebaiWebchatElement);
}

function normalizeConfig(config: WebchatConfig): ResolvedConfig {
  if (!config.company || !config.channelId) {
    throw new Error('company y channelId son obligatorios');
  }

  return {
    company: config.company,
    channelId: config.channelId,
    gatewayUrl: config.gatewayUrl || DEFAULTS.gatewayUrl,
    mode: config.mode || DEFAULTS.mode,
    position: config.position || DEFAULTS.position,
    title: config.title || DEFAULTS.title,
    placeholder: config.placeholder || DEFAULTS.placeholder,
    sendButtonText: config.sendButtonText || DEFAULTS.sendButtonText,
    launcherSymbol: config.launcherSymbol || DEFAULTS.launcherSymbol,
    launcherIconUrl: config.launcherIconUrl || '',
    userId: config.userId || randomId('usr'),
    sessionId: config.sessionId || randomId('ses'),
    autoOpen: config.autoOpen ?? config.mode === 'spotlight',
    zIndex: config.zIndex || DEFAULTS.zIndex,
    theme: config.theme,
    ui: config.ui,
  };
}

function mergeWithRemoteConfig(
  normalized: ResolvedConfig,
  input: WebchatConfig,
  remote: {
    mode?: 'widget' | 'spotlight';
    position?: string;
    title?: string;
    placeholder?: string;
    sendButtonText?: string;
    launcherSymbol?: string;
    launcherIconUrl?: string;
    theme?: {
      accentColor?: string;
      surfaceColor?: string;
      textColor?: string;
      headerTextColor?: string;
      userBubbleColor?: string;
      userTextColor?: string;
      assistantBubbleColor?: string;
      assistantTextColor?: string;
      composerBackgroundColor?: string;
      inputBackgroundColor?: string;
      inputTextColor?: string;
      sendButtonTextColor?: string;
    };
    ui?: {
      panelWidth?: number;
      panelHeight?: number;
      panelRadius?: number;
      bubbleRadius?: number;
      inputRadius?: number;
      fontSize?: number;
      launcherSize?: number;
      launcherShape?: 'circle' | 'rounded-square' | 'squircle' | 'hexagon';
      launcherBackgroundColor?: string;
    };
  },
): ResolvedConfig {
  const remoteTheme = remote.theme || {};
  const inputTheme = input.theme || {};
  const remoteUi = remote.ui || {};
  const inputUi = input.ui || {};

  return {
    ...normalized,
    mode: input.mode || remote.mode || normalized.mode,
    position: (input.position || remote.position || normalized.position) as ResolvedConfig['position'],
    title: input.title || remote.title || normalized.title,
    placeholder: input.placeholder || remote.placeholder || normalized.placeholder,
    sendButtonText: input.sendButtonText || remote.sendButtonText || normalized.sendButtonText,
    launcherSymbol: input.launcherSymbol || remote.launcherSymbol || normalized.launcherSymbol,
    launcherIconUrl: input.launcherIconUrl || remote.launcherIconUrl || normalized.launcherIconUrl,
    theme: {
      accentColor: inputTheme.accentColor || remoteTheme.accentColor || normalized.theme?.accentColor,
      surfaceColor: inputTheme.surfaceColor || remoteTheme.surfaceColor || normalized.theme?.surfaceColor,
      textColor: inputTheme.textColor || remoteTheme.textColor || normalized.theme?.textColor,
      headerTextColor: inputTheme.headerTextColor || remoteTheme.headerTextColor || normalized.theme?.headerTextColor,
      userBubbleColor: inputTheme.userBubbleColor || remoteTheme.userBubbleColor || normalized.theme?.userBubbleColor,
      userTextColor: inputTheme.userTextColor || remoteTheme.userTextColor || normalized.theme?.userTextColor,
      assistantBubbleColor: inputTheme.assistantBubbleColor || remoteTheme.assistantBubbleColor || normalized.theme?.assistantBubbleColor,
      assistantTextColor: inputTheme.assistantTextColor || remoteTheme.assistantTextColor || normalized.theme?.assistantTextColor,
      composerBackgroundColor: inputTheme.composerBackgroundColor || remoteTheme.composerBackgroundColor || normalized.theme?.composerBackgroundColor,
      inputBackgroundColor: inputTheme.inputBackgroundColor || remoteTheme.inputBackgroundColor || normalized.theme?.inputBackgroundColor,
      inputTextColor: inputTheme.inputTextColor || remoteTheme.inputTextColor || normalized.theme?.inputTextColor,
      sendButtonTextColor: inputTheme.sendButtonTextColor || remoteTheme.sendButtonTextColor || normalized.theme?.sendButtonTextColor,
    },
    ui: {
      panelWidth: inputUi.panelWidth || remoteUi.panelWidth || normalized.ui?.panelWidth,
      panelHeight: inputUi.panelHeight || remoteUi.panelHeight || normalized.ui?.panelHeight,
      panelRadius: inputUi.panelRadius || remoteUi.panelRadius || normalized.ui?.panelRadius,
      bubbleRadius: inputUi.bubbleRadius || remoteUi.bubbleRadius || normalized.ui?.bubbleRadius,
      inputRadius: inputUi.inputRadius || remoteUi.inputRadius || normalized.ui?.inputRadius,
      fontSize: inputUi.fontSize || remoteUi.fontSize || normalized.ui?.fontSize,
      launcherSize: inputUi.launcherSize || remoteUi.launcherSize || normalized.ui?.launcherSize,
      launcherShape: inputUi.launcherShape || remoteUi.launcherShape || normalized.ui?.launcherShape,
      launcherBackgroundColor: inputUi.launcherBackgroundColor || remoteUi.launcherBackgroundColor || normalized.ui?.launcherBackgroundColor,
    },
  };
}

export class KeebaiWebchatInstance {
  constructor(private readonly element: KeebaiWebchatElement) {}

  setOpen(value: boolean): void {
    this.element['setOpen'](value);
  }

  destroy(): void {
    this.element.remove();
  }
}

export interface KeebaiWebchatGlobal {
  init: (config: WebchatConfig) => KeebaiWebchatInstance;
}

const API: KeebaiWebchatGlobal = {
  init: (config: WebchatConfig) => {
    const el = document.createElement('keebai-webchat') as KeebaiWebchatElement;
    el.config = normalizeConfig(config);
    document.body.appendChild(el);
    void el.bootstrap(config);
    return new KeebaiWebchatInstance(el);
  },
};

declare global {
  interface Window {
    KeebaiWebchat?: KeebaiWebchatGlobal;
  }
}

window.KeebaiWebchat = API;

export default API;
